<script setup lang="ts">
import { useI18n } from "vue-i18n";
import moment from "moment";
import "moment/dist/locale/da";
import { useQuery } from "@vue/apollo-composable";
import { useAccessGroupStore } from "~/composables/stores/accessGroup";
import { AssetManagementAccessGroupsDocument, AssetManagementCurrentAccessGroupDocument } from "~/graphql/generated/graphql";
import { setUser } from "@sentry/vue";

const i18n = useI18n();
const accessGroupStore = useAccessGroupStore();
const me = useMe();
const ampli = useAmplitude();

const searchVisible = ref(false);

const hasIntegrationPermission = useHasPermissions(PERMISSIONS.INTEGRATION_READ).result;
const accessGroupsQuery = useQuery(AssetManagementAccessGroupsDocument);
const currentAccessGroupQuery = useQuery(AssetManagementCurrentAccessGroupDocument, undefined, () => ({ enabled: !accessGroupsQuery.loading.value }));

const currentAccessGroup = computed(() => currentAccessGroupQuery.result.value?.assetManagementCurrentAccessGroup);
const accessGroups = computed(() => accessGroupsQuery.result.value?.assetManagementAccessGroups ?? []);
const accessGroupsSorted = computed(() => [...(accessGroups.value ?? [])].sort((a, b) => a.name.localeCompare(b.name)));
const showAccessGroups = computed(() => (accessGroupsQuery.loading.value || accessGroupsQuery.error.value ? false : accessGroups.value.length > 1));

const localePrefsQuery = usePreferencesNamespace("preferenceNamespaceLocale");
const locale = computed(() => localePrefsQuery.result.value?.locale);

const panelFocusBlockAll = ref(false);

watch(
  computed(() => accessGroupStore.accessGroup),
  async (next, prev) => {
    // If the access group changes, we need to refetch the data
    // so we navigate to portfolio route or refresh the current route
    if (next && next !== prev) {
      reloadNuxtApp({ force: true, path: "/" });
    }
  }
);

watch(
  computed(() => hasIntegrationPermission.value),
  async (val) => {
    if (val) {
      try {
        const menuWarnings = await useSideMenuWarnings();
        useMenuStore().setWarnings(menuWarnings.warnings);
      } catch {}
    }
  }
);

const handleKeydown = (event: KeyboardEvent) => {
  if (event.altKey && event.code === "KeyF") {
    setTimeout(() => {
      const searchInput: HTMLInputElement | undefined = (document.querySelectorAll(".chrome-container-search input") as NodeListOf<HTMLInputElement>)[0];

      if (!searchInput) return;

      searchInput.focus();
    }, 0);
  }

  return false;
};

onMounted(() => window.addEventListener("keydown", handleKeydown));

onUnmounted(() => window.removeEventListener("keydown", handleKeydown));

const toggleSearch = () => {
  searchVisible.value = !searchVisible.value;
};

watch(
  locale,
  (v) => {
    if (v) {
      i18n.locale.value = v;
      // Map da-DK to da for moment
      const momentLocale = v === "da-DK" ? "da" : v;
      moment.locale(momentLocale);
    }
  },
  { immediate: true }
);

watch(
  () => me.result.value?.me,
  (me) => {
    if (me) {
      /** Sentry */
      try {
        setUser({
          username: me.name,
          email: me.email,
          id: me.id,
          orgId: me.orgId,
          organization: me.organization,
          admin: me.admin,
        });
      } catch {}

      /** Amplitude */
      try {
        ampli.identify(me.id, {
          name: me.name,
          orgId: me.organization?.id,
          orgName: me.organization?.name,
        });
      } catch {}
    }
  },
  { immediate: true }
);
</script>

<template>
  <div :key="locale" style="display: flex; flex-direction: column; user-select: none">
    <div :class="$style.containerPortal">
      <div :class="['container-portal-height-limit', $style.containerPortalHeightLimit]">
        <portal-target name="default" />
      </div>
    </div>

    <Chrome :panelFocusBlockAll="panelFocusBlockAll">
      <template #sideMenu>
        <SideMenu
          :currentAccessGroup="currentAccessGroup"
          :showAccesGroups="showAccessGroups"
          :accessGroups="accessGroupsSorted"
          @toggle-search="toggleSearch"
        />
      </template>

      <template #content>
        <NuxtPage />
      </template>
    </Chrome>
  </div>
</template>

<style module>
.containerPortal {
  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
}

.containerPortalHeightLimit {
  position: relative;
  height: calc(100% - 4rem);
  width: 100%;
}
</style>

<style>
html body .__nuxt-error-page {
  background-color: transparent !important;
}

html body .__nuxt-error-page .error {
  background-color: var(--color-pink-900) !important;
  padding: 1rem !important;
  border-radius: var(--border-radius-medium) !important;
  box-shadow: var(--box-shadow-large) !important;
}

html body .__nuxt-error-page .error > svg {
  display: none !important;
}

html body .__nuxt-error-page .error .logo {
  display: none !important;
}

html body .__nuxt-error-page .error .title,
html body .__nuxt-error-page .error .description {
  margin: 0 !important;
  padding: 0 !important;
  font-family: "GerstnerProgrammFSL";
  font-size: 16px !important;
  font-weight: 500 !important;
  line-height: 16px !important;
  color: var(--color-white) !important;
}

html body .__nuxt-error-page .error .description {
  line-height: calc(16px * 1.4);
}

html body .__nuxt-error-page .error .title {
  font-weight: 700 !important;
  font-size: 14px !important;
  margin-bottom: 0.5rem !important;
}

dialog {
  z-index: 9999;
  width: 80vw;
  background-color: var(--color-white);
  border: none;
  margin-left: auto;
  margin-right: auto;
  margin-top: auto;
  margin-bottom: auto;
  border-radius: var(--border-radius-large);
  box-shadow: var(--box-shadow-large);
}

dialog::backdrop {
  background: rgba(0, 0, 0, 0.4);
}
</style>
